import React, { useCallback, useState } from 'react';

import { PremiumFeatureGateModal } from '../components/_molecules/BlockedModals';
import useAccessBadge from './useAccessBadge';
import { useMetrics } from './useMetrics';
import { usePaywallEmailTracking } from './usePaywallEmailTracking';

// State/region is kept on the metadata payload for analytics + BI continuity.
// The new modal itself only branches on US vs International — state-specific
// stat content ships inside the home/trial modals, not here.
type PaywallVariant = 'default' | 'TX' | 'CA';

function isPaywallVariant(value: string | undefined): value is PaywallVariant {
  return value === 'TX' || value === 'CA';
}

function usePaywallPrompt({
  startState,
  onClose,
  mode,
  addonFormUrl,
}: {
  startState?: { screen: string };
  onClose?: () => void;
  /**
   * Which variant of the gate to render. Defaults to the standard school-plan
   * gate; `'curriculum-addon'` renders the "add-on to your school plan" variant
   * for users already on the plan. See PremiumFeatureGateModal.
   */
  mode?: 'default' | 'curriculum-addon';
  /** Per-curriculum request-page URL for the add-on CTA (mode='curriculum-addon'). */
  addonFormUrl?: string;
}) {
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const metrics = useMetrics();
  const trackPaywallEmail = usePaywallEmailTracking();
  const [show, setShow] = useState(false);
  // Source captured at prompt time. Threaded into the modal so CTA-click
  // events fired from inside it carry the same source as the email submit.
  // Falls back to `metadata.label`, then `metadata.screen`, then
  // `startState.screen` so callers passing only a label still get a useful
  // source label without a code change.
  const [paywallSource, setPaywallSource] = useState<string | undefined>(undefined);

  const isReady = userAccessBadge !== undefined;

  const shouldPromptPaywall = useCallback(() => {
    return false;
  }, []);

  const prompt = useCallback(
    (metadata?: PaywallMetadata) => {
      const paywallVariant = metadata?.variant;
      const derivedVariant =
        paywallVariant && isPaywallVariant(paywallVariant) ? paywallVariant : 'default';

      const resolvedSource = metadata?.label ?? metadata?.screen ?? startState?.screen;

      metrics.logEvent('Paywall.Prompted', {
        ...metadata,
        screen: startState?.screen,
        state: derivedVariant,
      });
      setPaywallSource(resolvedSource);
      setShow(true);
    },
    [metrics, startState?.screen],
  );

  const hide = useCallback(() => {
    setShow(false);
    onClose && onClose();
  }, [onClose]);

  const handleEmailSubmit = useCallback(
    (email: string) => {
      trackPaywallEmail(email, {
        paywallType: 'premium-feature-gate',
        source: paywallSource,
        screen: startState?.screen,
      });
    },
    [trackPaywallEmail, startState?.screen, paywallSource],
  );

  const country = userAccessBadge?.organization?.country;

  const component = (
    <PremiumFeatureGateModal
      visible={show}
      onClose={hide}
      country={country}
      source={paywallSource}
      screen={startState?.screen}
      onEmailSubmit={handleEmailSubmit}
      mode={mode}
      addonFormUrl={addonFormUrl}
    />
  );

  return {
    promptPaywall: (metadata?: PaywallMetadata) => prompt(metadata),
    hidePaywall: hide,
    paywallComponent: component,
    shouldPromptPaywall,
    isReady,
    isShowingPaywallPrompt: show,
    userAccessBadge,
    tenantName: userAccessBadge?.organization?.name,
  };
}

export type PaywallMetadata = {
  label?: string;
  description?: string;
  screen?: string;
  variant?: string;
};

export default usePaywallPrompt;
